// src/routes/reportJob/$reportJobId.tsx
import {
  Box,
  Container,
  Heading,
  Text,
  Grid,
  Spinner,
  Center,
  Table,
  Button,
  HStack,
  Flex,
  Badge,
} from "@chakra-ui/react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { FiClock, FiFileText, FiGrid, FiList, FiPause, FiPlay } from "react-icons/fi"
import { ApiError, ReportJobUpdate, ReportsService } from "@/client"
import { ProcessStateTag } from "@/components/ui/state-tag"
import { ReportBreadcrumb } from "@/components/Reports/ReportBreadcrumb"
import FeedBadges from "@/components/Reports/FeedBadges"
import {
  reportTypeOptions,
  scheduleOptions,
} from "@/types/report"
import Pagination from "@/components/Common/Pagination"
import { handleError } from "@/utils"
import useCustomToast from "@/hooks/useCustomToast"
import { SubmitHandler, useForm } from "react-hook-form"

export const Route = createFileRoute("/_layout/reportJob/$reportJobId")({
  component: Reports,
})

function Reports() {
  const { reportJobId } = Route.useParams()
  const navigate = useNavigate({ from: Route.fullPath })
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ["reports", reportJobId],
    queryFn: () =>
      ReportsService.listReportsByJobId({
        jobId: reportJobId,
      }),
    refetchInterval: 5000,
    refetchIntervalInBackground: false
  })

  const { data: metadata } = useQuery({
    queryKey: ['reportMetadata', reportJobId],
    queryFn: () => ReportsService.listReportJobs({ reportJobId: reportJobId }),
  })

  const reportTypeLabel =
    reportTypeOptions.find((opt) => opt.value === metadata?.[0]?.report_type)
      ?.label || "타입 없음"

  // 스케줄 라벨 찾기
  const scheduleLabel =
    scheduleOptions.find((opt) => opt.value === metadata?.[0]?.schedule)
      ?.label || "스케줄 없음"

  const [page, setPage] = useState(1)
  const pageConfig = {
    grid: { itemsPerPage: 12 },
    list: { itemsPerPage: 6 },
  }
  const currentConfig = pageConfig[viewMode]
  const totalPages = Math.ceil(reports.length / currentConfig.itemsPerPage)
  const paginatedJobs = reports.slice(
    (page - 1) * currentConfig.itemsPerPage,
    page * currentConfig.itemsPerPage
  )

  const handleChangeViewMode = (mode: "grid" | "list") => {
    setViewMode(mode)
  
    const itemsPerPage = pageConfig[mode].itemsPerPage
    const newTotalPages = Math.ceil(reports.length / itemsPerPage)
  
    // 현재 페이지가 넘어가면 보정
    setPage((prevPage) => Math.min(prevPage, newTotalPages))
  }
  
  const {
    handleSubmit,
    reset,
    setValue,
  } = useForm<ReportJobUpdate>({
    mode: "onBlur",
    criteriaMode: "all",
  })
  
  useEffect(() => {
    if (metadata && metadata[0]) {
      reset({
        feed_ids: metadata[0].feed_ids,
        report_job_name: metadata[0].report_job_name,
        description: metadata[0].description,
        report_type: metadata[0].report_type,
        schedule: metadata[0].schedule,
      })
    }
  }, [metadata])
  
  const { showSuccessToast } = useCustomToast()
  const queryClient = useQueryClient()
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const mutation = useMutation({
    mutationFn: ({ jobId, payload }: { jobId: string; payload: ReportJobUpdate }) =>
      ReportsService.updateReportJob({ jobId, requestBody: payload }),
  
    onSuccess: () => {
      showSuccessToast("리포트 작업 스케줄링이 수정되었습니다.")

      queryClient.invalidateQueries({
        queryKey: ["reportMetadata", reportJobId],
      })

      queryClient.invalidateQueries({ queryKey: ["reportJobs"] })
      setHasSubmitted(false)
    },
  
    onError: (err: ApiError) => {
      handleError(err)
      setHasSubmitted(false)
    },
  })

  const onSubmit: SubmitHandler<ReportJobUpdate> = (formData) => {
    if (hasSubmitted) return
    setHasSubmitted(true)

    mutation.mutate({
      jobId: metadata?.[0]?.id ?? "",
      payload: formData,
    })
  }
  
  if (isLoading) {
    return (
      <Center w="100%" py={10}>
        <Spinner />
      </Center>
    )
  }

  return (
    <Container maxW="7xl" mt={4} display="flex" flexDirection="column">
    <Box flex="1" overflowY="auto">
      <ReportBreadcrumb
        reportJobId={reportJobId}
        reportJobName={reports?.[0]?.report_job_name || "리포트 생성 작업"}
      />
      <Flex justifyContent="space-between" mb={4} alignItems="baseline">
        <Heading size="lg">
          {metadata?.[0]?.report_job_name} 리포트 상세 보기
        </Heading>
        <HStack>
          <Button
            variant={viewMode === "grid" ? "solid" : "ghost"}
            size="sm"
            onClick={() => handleChangeViewMode("grid")}
          >
            <FiGrid />
          </Button>
          <Button
            variant={viewMode === "list" ? "solid" : "ghost"}
            size="sm"
            onClick={() => handleChangeViewMode("list")}
          >
            <FiList />
          </Button>
        </HStack>
      </Flex>

      <HStack my={4}>
        <Badge px={3} py={1} borderRadius="md">
          <HStack>
            <FiFileText />
            <Text>{reportTypeLabel}</Text>
          </HStack>
        </Badge>
        <Badge px={3} py={1} borderRadius="md">
          <HStack>
            <FiClock />
            <Text>{scheduleLabel}</Text>
          </HStack>
        </Badge>
        
        {metadata?.[0]?.schedule === "stop" ? (
          // ▶ START 버튼
          <FiPlay
            style={{ fontSize: "16px", cursor: "pointer" }}
            onClick={() => {
              setValue("schedule", metadata?.[0]?.previous_schedule || "weekly")
              handleSubmit(onSubmit)()
            }}
          />
        ) : metadata?.[0]?.schedule !== "once" ? (
          // ⏸ PAUSE 버튼
          <FiPause
            style={{ fontSize: "16px", cursor: "pointer" }}
            onClick={() => {
              setValue("schedule", "stop")
              handleSubmit(onSubmit)()
            }}
          />
        ) : null}
      </HStack>

      <FeedBadges feeds={metadata?.[0]?.feeds || []}/>

      <Box w="full" bg="gray.50" p={4} borderRadius="md" my={4}>
        <Text fontSize="sm" color="gray.700" wordBreak="break-all">
          {metadata?.[0]?.description }
        </Text>
      </Box>
      {reports?.length === 0 ? (
        <Center py={10}>
          <Text>리포트가 없습니다.</Text>
        </Center>
      ) : viewMode === "grid" ? (
        <Grid
          templateColumns={{
            base: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={6}
          p={2}
        >
          {paginatedJobs?.map((report) => (
            <Box
              key={report.id}
              p={4}
              borderWidth={1}
              borderRadius="md"
              boxShadow="sm"
              _hover={{ shadow: "md", cursor: "pointer" }}
              onClick={() => navigate({ to: "/report/$reportId", params: { reportId: report.id } })}
            >
              <Text fontWeight="semibold">{report.report_name || "제목없음"}</Text>
              <Text fontSize="sm" color="gray.500" lineClamp="2">
                {report.report_description || "설명 없음"}
              </Text>
              <Text fontSize="xs" mt={2} color="gray.400">
                생성일: {new Date(report.created_at).toLocaleDateString()}
              </Text>
              <ProcessStateTag state={report.status || "idle"} />
            </Box>
          ))}
        </Grid>
      ) : (
        <Table.Root size={{ base: "sm", md: "md" }}>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader w="20%">제목</Table.ColumnHeader>
              <Table.ColumnHeader w="35%">설명</Table.ColumnHeader>
              <Table.ColumnHeader w="7%">생성일</Table.ColumnHeader>
              <Table.ColumnHeader w="5%">상태</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paginatedJobs?.map((report) => (
              <Table.Row
                key={report.id}
                h="82px"
                _hover={{ bg: "gray.50", cursor: "pointer" }}
                onClick={() => navigate({ to: "/report/$reportId", params: { reportId: report.id } })}
              >
                <Table.Cell>{report.report_name || "제목없음"}</Table.Cell>
                <Table.Cell>
                  <Text
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    lineClamp="3"
                  >
                    {report.report_description || "설명 없음"}
                  </Text>
                </Table.Cell>
                <Table.Cell>{new Date(report.created_at).toLocaleDateString()}</Table.Cell>
                <Table.Cell>
                  <ProcessStateTag state={report.status || "idle"} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
      </Box>
      <Box py={3} borderTop="1px solid" borderColor="gray.200" bg="white" textAlign="center">
        <Pagination page={page} setPage={setPage} totalPages={totalPages} />
      </Box>
    </Container>
  )
}
